import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Check, IdCard, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SpecialtyBadge } from './SpecialtyBadge';
import { CLINICAL_FOCUS_TRACKS, LICENSE_TYPES } from '../../lib/clinicianTracks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FEATURED = new Set(CLINICAL_FOCUS_TRACKS.filter((t) => t.featured).map((t) => t.id));
const ADVANCED_LICENSES = new Set(['NP', 'MD']);

/** Mirror of server._clinician_badges so the clinician sees what they'll earn, live. */
const computeBadges = ({ verified, licenseType, focus }) => {
  const badges = [];
  if (verified && focus.length > 0) badges.push('verified_specialist');
  const hasFeatured = focus.some((f) => FEATURED.has(f));
  if (hasFeatured || ADVANCED_LICENSES.has((licenseType || '').toUpperCase())) badges.push('advanced_clinical_scope');
  return badges;
};

/**
 * Clinician practice-profile editor — edit the public-facing details that drive
 * marketplace discovery, AI matching and credential badges (NPI + Clinical Focus Areas).
 * Posts to PUT /api/clinician/public-profile (validates NPI + focus ids server-side).
 */
export const PracticeProfileCard = ({ getAuthHeader, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verified, setVerified] = useState(false);
  const [form, setForm] = useState({
    headline: '', bio: '', license_type: 'RN', years_of_experience: '',
    hourly_rate: '', city: '', state: '', npi: '', tax_id: '', taxonomy_code: '', clinical_focus_areas: [],
  });

  useEffect(() => {
    axios.get(`${API}/clinician/profile`, getAuthHeader())
      .then((r) => {
        const p = r.data || {};
        setVerified(p.verification_status === 'verified' || !!p.license_verified);
        setForm({
          headline: p.headline || '',
          bio: p.bio || '',
          license_type: (p.license_type || 'RN'),
          years_of_experience: p.years_of_experience ?? '',
          hourly_rate: p.hourly_rate ?? '',
          city: p.city || '',
          state: p.state || '',
          npi: p.npi || '',
          tax_id: p.tax_id || '',
          taxonomy_code: p.taxonomy_code || '',
          clinical_focus_areas: p.clinical_focus_areas || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleFocus = (id) =>
    setForm((prev) => ({
      ...prev,
      clinical_focus_areas: prev.clinical_focus_areas.includes(id)
        ? prev.clinical_focus_areas.filter((x) => x !== id)
        : [...prev.clinical_focus_areas, id],
    }));

  const badges = computeBadges({ verified, licenseType: form.license_type, focus: form.clinical_focus_areas });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        headline: form.headline || null,
        bio: form.bio || null,
        license_type: form.license_type,
        years_of_experience: form.years_of_experience === '' ? null : Number(form.years_of_experience),
        hourly_rate: form.hourly_rate === '' ? null : Number(form.hourly_rate),
        city: form.city || null,
        state: form.state || null,
        npi: form.npi || null,
        tax_id: form.tax_id || null,
        taxonomy_code: form.taxonomy_code || null,
        clinical_focus_areas: form.clinical_focus_areas,
      };
      await axios.put(`${API}/clinician/public-profile`, payload, getAuthHeader());
      toast.success('Practice profile saved');
      if (onSaved) onSaved(payload);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your practice profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-cn-primary animate-spin" /></div>;
  }

  return (
    <div data-testid="practice-profile-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center flex-shrink-0">
            <IdCard className="w-5 h-5" />
          </div>
          <div>
            <p className="cn-eyebrow">Profile</p>
            <h3 className="cn-display text-lg text-cn-text dark:text-white leading-tight">Your practice profile</h3>
          </div>
        </div>
        {verified && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-cn-success whitespace-nowrap">
            <BadgeCheck className="w-3.5 h-3.5" /> License verified
          </span>
        )}
      </div>

      <p className="text-sm text-cn-text-muted mt-3">
        This powers your public profile, AI matches, and the badges families see. Keep it sharp.
      </p>

      {/* Live badge preview */}
      <div className="mt-4 p-3 rounded-2xl bg-cn-bg/60 border border-cn-border/60" data-testid="practice-profile-badges">
        <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Your badges</p>
        {badges.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {badges.map((b) => <SpecialtyBadge key={b} variant={b} />)}
          </div>
        ) : (
          <p className="text-xs text-cn-text-muted">
            {verified ? 'Add a clinical focus area below to earn the Verified Specialist badge.' : 'Once your license is verified and you add a clinical focus, you\u2019ll earn the Verified Specialist badge.'}
          </p>
        )}
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Headline</p>
          <Input
            value={form.headline}
            onChange={(e) => set('headline', e.target.value)}
            placeholder="e.g. Geriatric Nurse Practitioner · Chronic & hospice care"
            className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
            data-testid="practice-headline"
          />
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Bio</p>
          <Textarea
            value={form.bio}
            onChange={(e) => set('bio', e.target.value)}
            placeholder="Tell families about your experience, approach, and what makes your care special."
            className="min-h-[88px] rounded-2xl border-cn-border bg-white dark:bg-white/5"
            data-testid="practice-bio"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">License type</p>
            <Select value={form.license_type} onValueChange={(v) => set('license_type', v)}>
              <SelectTrigger className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5" data-testid="practice-license-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LICENSE_TYPES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Years experience</p>
            <Input
              type="number" min="0" max="70"
              value={form.years_of_experience}
              onChange={(e) => set('years_of_experience', e.target.value)}
              placeholder="e.g. 8"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
              data-testid="practice-years"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Rate $/hr</p>
            <Input
              type="number" min="0"
              value={form.hourly_rate}
              onChange={(e) => set('hourly_rate', e.target.value)}
              placeholder="120"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
              data-testid="practice-rate"
            />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">City</p>
            <Input
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              placeholder="Austin"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
              data-testid="practice-city"
            />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">State</p>
            <Input
              value={form.state}
              onChange={(e) => set('state', e.target.value)}
              placeholder="TX"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
              data-testid="practice-state"
            />
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">NPI number</p>
          <Input
            inputMode="numeric" maxLength={10}
            value={form.npi}
            onChange={(e) => set('npi', e.target.value.replace(/\D/g, ''))}
            placeholder="10-digit National Provider Identifier"
            className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5 font-mono tracking-wide"
            data-testid="practice-npi"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Tax ID / EIN</p>
            <Input
              value={form.tax_id}
              onChange={(e) => set('tax_id', e.target.value)}
              placeholder="99-9999999"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5 font-mono"
              data-testid="practice-tax-id"
            />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Taxonomy Code</p>
            <Input
              value={form.taxonomy_code}
              onChange={(e) => set('taxonomy_code', e.target.value)}
              placeholder="e.g. 163W00000X"
              className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5 font-mono"
              data-testid="practice-taxonomy"
            />
          </div>
        </div>


        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Clinical focus areas</p>
          <div className="flex flex-wrap gap-2" data-testid="practice-focus-picker">
            {CLINICAL_FOCUS_TRACKS.map((t) => {
              const on = form.clinical_focus_areas.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleFocus(t.id)}
                  aria-pressed={on}
                  data-testid={`practice-focus-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    on ? 'bg-cn-primary text-white border-cn-primary shadow-sm'
                       : 'bg-white dark:bg-white/5 text-cn-text border-cn-border hover:border-cn-primary/40'
                  }`}
                >
                  {on && <Check className="w-3 h-3" />}
                  <t.Icon className="w-3.5 h-3.5" /> {t.name}
                  {t.featured && (
                    <span className={`text-[9px] uppercase font-bold tracking-wide ${on ? 'text-cn-terracotta-200' : 'text-cn-terracotta-600'}`}>· High demand</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="cn-btn cn-btn-primary w-full mt-5 !py-2.5" data-testid="practice-profile-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save practice profile'}
      </Button>
    </div>
  );
};

export default PracticeProfileCard;
